import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ArrowLeft, Upload, Trash2, Copy, Download, FileText, CheckCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DatasheetManagement = ({ onBack }) => {
  const navigate = useNavigate();
  const [datasheets, setDatasheets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("full_height_turnstiles");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadSKU, setUploadSKU] = useState("");
  const [copiedUrl, setCopiedUrl] = useState("");

  const categories = [
    { value: "full_height_turnstiles", label: "Full Height Turnstiles" },
    { value: "atlas", label: "Atlas Access Control" },
    { value: "barriers", label: "Parking Barriers" },
    { value: "comet", label: "Comet Speedlane" }
  ];

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    loadDatasheets();
  }, [selectedCategory]);

  const loadDatasheets = async () => {
    setLoading(true);
    setError("");
    try {
      // List files in the category directory
      const response = await axios.get(`${API}/datasheets/list/${selectedCategory}`, getAuthHeaders());
      setDatasheets(response.data.files || []);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load datasheets");
      setDatasheets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      setError("Please select a file to upload");
      return;
    }

    if (!uploadSKU.trim()) {
      setError("Please enter a product SKU");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('category', selectedCategory);
      formData.append('sku', uploadSKU);

      const response = await axios.post(`${API}/datasheets/upload`, formData, {
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });

      const linkedCount = response.data.linked_skus?.length || 1;
      setSuccess(`✓ Datasheet uploaded successfully! Linked to ${linkedCount} SKU${linkedCount > 1 ? 's' : ''}: ${response.data.linked_skus?.join(', ') || uploadSKU}`);
      setUploadFile(null);
      setUploadSKU("");
      loadDatasheets();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to upload datasheet");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Are you sure you want to delete ${filename}?`)) {
      return;
    }

    setLoading(true);
    setError("");
    try {
      await axios.delete(`${API}/datasheets/${selectedCategory}/${filename}`, getAuthHeaders());
      setSuccess(`Deleted: ${filename}`);
      loadDatasheets();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to delete datasheet");
    } finally {
      setLoading(false);
    }
  };

  const copyUrl = (filename) => {
    const url = `${BACKEND_URL}/datasheets/${selectedCategory}/${filename}`;
    navigator.clipboard.writeText(url);
    setCopiedUrl(filename);
    setSuccess(`URL copied to clipboard: ${filename}`);
    setTimeout(() => setCopiedUrl(""), 3000);
  };

  const downloadDatasheet = (filename) => {
    const url = `${BACKEND_URL}/datasheets/${selectedCategory}/${filename}`;
    window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Button 
          onClick={onBack || (() => navigate('/dashboard'))}
          variant="outline" 
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <Card>
          <CardHeader className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
            <CardTitle className="text-3xl">Datasheet Management</CardTitle>
            <CardDescription className="text-purple-100">
              Upload and manage product datasheets (Admin Only)
            </CardDescription>
          </CardHeader>

          <CardContent className="p-6">
            {/* Category Selection */}
            <div className="mb-6">
              <Label className="text-lg font-semibold mb-3 block">Select Product Category</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {categories.map(cat => (
                  <Button
                    key={cat.value}
                    onClick={() => setSelectedCategory(cat.value)}
                    variant={selectedCategory === cat.value ? "default" : "outline"}
                    className="w-full"
                  >
                    {cat.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Upload Section */}
            <Card className="mb-6 bg-slate-50">
              <CardHeader>
                <CardTitle className="text-xl">Upload New Datasheet</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="sku">Product SKU(s)</Label>
                    <Input
                      id="sku"
                      placeholder="e.g., MK-FHT-T2500-D-3 or MK-FHT-T2500-D-3,MK-FHT-T2500-D-3-FP,MK-FHT-T2500-D-3-BP"
                      value={uploadSKU}
                      onChange={(e) => setUploadSKU(e.target.value)}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      💡 Tip: Enter multiple SKUs separated by commas to link the same datasheet to all product variants
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="file">PDF File</Label>
                    <Input
                      id="file"
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setUploadFile(e.target.files[0])}
                    />
                  </div>
                  <Button onClick={handleUpload} disabled={loading} className="w-full">
                    <Upload className="w-4 h-4 mr-2" />
                    {loading ? "Uploading..." : "Upload Datasheet"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Messages */}
            {error && (
              <Alert className="mb-4 bg-red-50 border-red-300">
                <AlertDescription className="text-red-900">{error}</AlertDescription>
              </Alert>
            )}
            {success && (
              <Alert className="mb-4 bg-green-50 border-green-300">
                <AlertDescription className="text-green-900">{success}</AlertDescription>
              </Alert>
            )}

            {/* Datasheets List */}
            <div>
              <h3 className="text-xl font-semibold mb-4">
                {categories.find(c => c.value === selectedCategory)?.label} Datasheets
              </h3>

              {loading && <p className="text-slate-600">Loading...</p>}

              {!loading && datasheets.length === 0 && (
                <p className="text-slate-600">No datasheets uploaded for this category yet.</p>
              )}

              {!loading && datasheets.length > 0 && (
                <div className="space-y-3">
                  {datasheets.map((file, index) => (
                    <Card key={index} className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <FileText className="w-6 h-6 text-red-600" />
                          <div>
                            <p className="font-medium">{file}</p>
                            <p className="text-sm text-slate-600">
                              URL: /datasheets/{selectedCategory}/{file}
                            </p>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyUrl(file)}
                          >
                            {copiedUrl === file ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadDatasheet(file)}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(file)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DatasheetManagement;
